import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { jwtDecode } from 'jwt-decode'
import Loader from '@/components/Loader'
import { useWebSocket } from '@/context/WebSocketContext'
import { GroupProps, profileType } from './GroupList'
import DummyImage from '@/assets/dummy-profile-image.svg'

type Props = {
  groupInfo: GroupProps | null
  onClick: () => void
  onClickDetails: () => void
  goBack: () => void
}

type chatProps = {
  createdAt: string
  message: string
  senderFirstName: string
  senderId: string
  senderLastName: string
  senderProfilePic: string
  _id: string
}

interface GroupedMessages {
  date: string
  messages: chatProps[]
}

type userProps = {
  user: {
    _id: string
  }
}

const GroupChatDetails: React.FC<Props> = ({
  groupInfo,
  onClickDetails,
  goBack,
}) => {
  const { socket } = useWebSocket()
  // const fileInputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [chatData, setChatData] = useState<chatProps[]>([])
  const [chatHistory, setChatHistoryData] = useState<GroupedMessages[]>([])

  const jwtToken: string = localStorage.getItem('token') ?? ''
  const user = jwtDecode<userProps>(jwtToken)

  const groupMessagesByDate = (messages: chatProps[]): GroupedMessages[] => {
    const grouped: { [key: string]: chatProps[] } = {}

    messages.forEach((message) => {
      const date = new Date(message.createdAt)
      const today = new Date()
      let dateKey = ''

      if (date.toDateString() === today.toDateString()) {
        dateKey = 'Today'
      } else if (
        date.toDateString() ===
        new Date(today.setDate(today.getDate() - 1)).toDateString()
      ) {
        dateKey = 'Yesterday'
      } else {
        dateKey = moment(date).format('DD/MM/YYYY') // Format as needed
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(message)
    })

    return Object.entries(grouped).map(([date, messages]) => ({
      date,
      messages,
    }))
  }

  useEffect(() => {
    if (socket) {
      socket.emit('get-chat-history', {
        roomId: groupInfo ? groupInfo._id : undefined,
      })
    }
  }, [socket, groupInfo])

  useEffect(() => {
    if (!socket) return
    const handleChatHistory = (data: chatProps[]) => {
      setChatData(data)
      const sortedData = data.sort(
        (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
      )
      const groupedData = groupMessagesByDate(sortedData)
      setChatHistoryData(groupedData)
      setIsLoading(false)
    }

    socket.on('chat-history', handleChatHistory)

    return () => {
      socket.off('chat-history', handleChatHistory)
    }
  }, [socket])

  useEffect(() => {
    if (socket) {
      const handleReceiveMessage = (data: chatProps[]) => {
        const tempChatData = [...chatData, ...data]
        const sortedData = tempChatData.sort(
          (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
        )
        setChatData(tempChatData)
        const updatedChat = groupMessagesByDate(sortedData)
        setChatHistoryData(updatedChat)
      }

      socket.on('receive-message', handleReceiveMessage)

      return () => {
        socket.off('receive-message', handleReceiveMessage)
      }
    }
  }, [socket, chatData])

  const sendMessageWithAck = async (msg: string) => {
    if (socket) {
      socket.emit('send-message', {
        roomId: groupInfo ? groupInfo._id : undefined,
        message: msg,
      })
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value)
  }

  const submitMessage = () => {
    sendMessageWithAck(inputValue)
    setInputValue('')
    if (inputRef.current) {
      inputRef.current.style.height = '56px'
      inputRef.current.blur()
    }
  }

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [chatHistory])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="h-[calc(100vh-200px)] md:h-[calc(100vh-140px)] relative">
      <div onClick={goBack} className="relative cursor-pointer">
        <img
          className="absolute left-0 top-0"
          src="./Left-icon.svg"
          alt="back icon"
        />
      </div>
      <div className="flex flex-col items-center justify-center gap-1">
        <div
          onClick={onClickDetails}
          className="flex cursor-pointer h-[40px] sm:h-[46px]"
        >
          {groupInfo &&
            groupInfo?.profileIds.length > 0 &&
            groupInfo.profileIds
              ?.slice(0, 4)
              ?.map((profile: profileType, index: number) => {
                return (
                  <img
                    key={index}
                    src={profile.profilePic ? profile.profilePic : DummyImage}
                    className={`z-index-${(4 - index) * 10} ${index > 0 ? '-ml-3 sm:-ml-4' : ''} w-[40px] h-[40px] sm:w-[55px] sm:h-[55px] object-cover border-4 border-[#ffffff] bg-[#ffffff1a] rounded-full`}
                  />
                )
              })}
        </div>
        <p className="text-[18px] sm:text-[24px] capitalize font-bold mt-4">
          {groupInfo && groupInfo.groupName}
        </p>
      </div>

      <div
        ref={listRef}
        className="flex flex-col h-[60vh] sm:h-96 overflow-y-auto mt-10 pr-[15px] pb-20"
      >
        {chatHistory.map((group, index) => (
          <div key={index}>
            <h3 className="my-4 bg-[#365F91] px-4 py-2 rounded-lg w-fit mx-auto">
              {group?.date}
            </h3>
            <div>
              {group?.messages?.length > 0 &&
                group.messages.map((chat: chatProps) => {
                  const isSender = user?.user?._id === chat?.senderId

                  return (
                    <div
                      key={chat._id} // Use chat._id for a unique key
                      className={`flex mb-[25px] ${isSender ? 'justify-end' : ''}`}
                    >
                      {!isSender && (
                        <img
                          src={
                            chat?.senderProfilePic
                              ? chat.senderProfilePic
                              : DummyImage
                          }
                          alt="User Avatar"
                          className="w-[36px] h-[36px] sm:w-[46px] sm:h-[46px] object-cover rounded-full border-4 border-[#ffffff]"
                        />
                      )}
                      <div
                        className={`max-w-[75%] sm:max-w-[45%] ${isSender ? 'mr-[11px]' : 'ml-[11px]'} rounded-[13px] ${isSender ? 'rounded-tr-[0] bg-[#365F91]' : 'rounded-tl-[0] bg-gradient-to-tl from-[#FFFFFF4D] via-[#FFFFFF4D] to-[#ffffff1a]'}`}
                      >
                        <div className="h-full w-full rounded-[13px] text-[#FFFFFC] px-4 py-[14px] text-left text-base sm:text-xl font-normal">
                          <p className="break-all whitespace-pre-wrap">
                            {chat.message.startsWith('https://') ? (
                              <img src={chat?.message} alt="uploaded" />
                            ) : (
                              chat?.message
                            )}
                          </p>
                          <p className="text-[#959595] font-normal text-sm text-right">
                            {moment(chat?.createdAt).format('h:mm a')}
                          </p>
                        </div>
                      </div>
                      {isSender && (
                        <img
                          src={
                            chat?.senderProfilePic
                              ? chat.senderProfilePic
                              : DummyImage
                          }
                          alt="User Avatar"
                          className="w-[36px] h-[36px] sm:w-[46px] sm:h-[46px] object-cover rounded-full border-4 border--[#ffffff]"
                        />
                      )}
                    </div>
                  )
                })}
            </div>
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 right-0 w-full mx-auto">
        <div className="w-full bg-grad p-[1px] before:rounded-[25px] relative bottom-0 overflow-hidden">
          <div className="relative rounded-[25px] backdrop-blur-lg bg-[#1D1D1D] flex items-start py-1 px-4">
            <textarea
              rows={1}
              ref={inputRef}
              placeholder="Type message here ....."
              style={{ height: 'auto', resize: 'none' }}
              value={inputValue}
              onChange={handleInputChange}
              onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                const target = e.currentTarget as HTMLTextAreaElement
                target.style.height = 'auto'
                target.style.height = `${target.scrollHeight}px`
              }}
              className="self-center bg-[#0000] text-xl overflow-hidden flex-grow border-none outline-none rounded-lg leading-6 p-1 max-h-[120px] overflow-y-auto "
            />
            <button
              onClick={() => submitMessage()}
              className="text-white rounded-full px-4 py-4 my-2 mr-2 bg-[#FB5012] self-end"
            >
              <img src="./send-icon.svg" alt="send" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
export default GroupChatDetails
